// React / Next
import { useRef, useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';

// Components
import { TextMaskReveal } from '../ui/TextMaskReveal/TextMaskReveal';
import { VideoControls } from '../VideoControls/VideoControls';
import { AnnouncementBanner } from '../AnnouncementBanner/AnnouncementBanner';

// Hooks
import { useTrackEvent } from '../../hooks/useTrackEvent';

// Types
import type { AnnouncementBannerData } from '../../lib/sanity';

type HeroSectionProps = {
  banner?: AnnouncementBannerData | null;
};

export const HeroSection = ({ banner }: HeroSectionProps) => {
  const trackEvent = useTrackEvent();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showSoundHint, setShowSoundHint] = useState(true);

  // Handle play/pause toggle
  const handlePlayPause = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(() => {});
    }
    setIsPlaying(!isPlaying);
  };

  // Handle mute toggle
  const handleMuteToggle = () => {
    if (!videoRef.current) return;

    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
    setShowSoundHint(false);
  };

  // Dismiss sound hint on click (unmute) or after 5 seconds
  const handleSoundHintClick = () => {
    handleMuteToggle();
  };

  // Handle seeking
  const handleSeek = (time: number) => {
    if (!videoRef.current) return;

    videoRef.current.currentTime = time;
    setCurrentTime(time);
  };

  // Set up video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    // Set initial duration if already loaded
    if (video.duration) {
      setDuration(video.duration);
    }

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, []);

  // Play video when fully visible in viewport
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          }
        });
      },
      { threshold: 1.0 }
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Auto-dismiss sound hint after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowSoundHint(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      id="hero"
      className="flex flex-col items-center gap-[var(--spacing-64)] px-[var(--spacing-24)] py-[var(--spacing-64)] w-full max-w-[1224px] max-md:gap-[var(--spacing-48)] max-md:px-[var(--spacing-16)] max-md:py-[var(--spacing-48)]"
    >
      {/* Announcement Banner */}
      {banner?.isActive && banner.title && banner.href && (
        <AnnouncementBanner
          label={banner.label}
          title={banner.title}
          href={banner.href}
        />
      )}

      {/* Hero Content */}
      <div className="flex flex-col items-center gap-[var(--spacing-24)] text-center">
        {/* Display/Regular */}
        <TextMaskReveal
          as="h1"
          variant="display"
          className="text-blue-1000 text-center max-w-[804px]"
          staggerDelay={0.12}
          duration={0.8}
          delay={0.1}
          animateOnMount
        >
          Trained Like An Accountant.
          {'\n'}
          Built Like A Teammate.
        </TextMaskReveal>

        {/* Heading 2/Regular */}
        <TextMaskReveal
          as="p"
          variant="h2"
          className="text-blue-1000 text-center w-full"
          staggerDelay={0.1}
          duration={0.8}
          delay={0.5}
          animateOnMount
        >
          Archie is the AI-powered staff accountant that does the manual work
          {'\n'}
          requiring context, judgment, and adaptability,
          {'\n'}
          not just following a script.
        </TextMaskReveal>
      </div>

      {/* CTA Button */}
      <Link
        to="/contact-sales"
        onClick={() =>
          trackEvent('cta_clicked', {
            cta_text: 'Contact Sales',
            cta_location: 'hero_section',
            cta_href: '/contact-sales',
          })
        }
      >
        <button className="inline-flex items-center justify-center gap-[var(--spacing-8)] px-[var(--spacing-16)] py-[var(--spacing-12)] rounded-[var(--radius-8)] font-body text-[16px] leading-[24px] font-normal transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 bg-orange-800 text-white hover:bg-orange-1000 focus:ring-orange-800">
          Contact Sales
        </button>
      </Link>

      {/* Hero Video */}
      <div className="group relative w-full max-w-[1200px] aspect-[1015/560] rounded-16 overflow-hidden">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          loop
          muted
          playsInline
          aria-label="Archie AI assistant demonstration video"
        >
          <source
            src={
              import.meta.env.VITE_HERO_VIDEO_URL || '/video/Archie-Full-V3.mp4'
            }
            type="video/mp4"
          />
          Your browser does not support the video tag.
        </video>

        {/* Sound on hint - small button in top-right corner, dismisses on click or after 5s */}
        {showSoundHint && isMuted && (
          <button
            type="button"
            onClick={handleSoundHintClick}
            className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm font-body text-[13px] leading-[18px] text-white transition-all hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-white/50"
            aria-label="Turn on sound for full effect"
          >
            <VolumeMutedSmallIcon className="w-[18px] h-[18px] shrink-0" />
            <span>Sound on</span>
          </button>
        )}

        {/* Video Controls - hidden by default, visible on hover */}
        <div className="absolute bottom-0 left-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <VideoControls
            isPlaying={isPlaying}
            isMuted={isMuted}
            currentTime={currentTime}
            duration={duration}
            onPlayPause={handlePlayPause}
            onMuteToggle={handleMuteToggle}
            onSeek={handleSeek}
          />
        </div>
      </div>
    </section>
  );
};

const VolumeMutedSmallIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M11 5 6 9H2v6h4l5 4V5Z" />
    <line x1="23" x2="17" y1="9" y2="15" />
    <line x1="17" x2="23" y1="9" y2="15" />
  </svg>
);
